import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, patch, del } from "../../lib/request"

/** Unwrap GET /client-note/notes payloads (array or `{ data: [] }`). */
export function extractClientNotesList(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.notes)) return payload.notes
  if (Array.isArray(payload.content)) return payload.content
  return []
}

/**
 * Maps GET /client-note/notes array items to the shape the UI expects (`id`, `noteType.id`, etc.).
 * API fields: noteId, noteTypeId, notesTypeName, insertedDatetime, accessLevel, …
 */
export function normalizeClientNoteFromApi(raw) {
  if (!raw || typeof raw !== "object") return raw
  const noteId = raw.noteId ?? raw.id
  /** Some APIs return `typeId` (not `noteTypeId`) — support both so Notes tab filters work. */
  const noteTypeId = raw.noteTypeId ?? raw.typeId ?? raw.noteType?.id
  const tid = noteTypeId != null && noteTypeId !== "" ? Number(noteTypeId) : null
  const createdById =
    raw.createdById ?? raw.created_by_id ?? raw.createdBy?.id ?? null
  return {
    ...raw,
    id: noteId,
    noteId,
    createdById,
    title: raw.title ?? "",
    note: raw.note ?? "",
    insertedDatetime: raw.insertedDatetime ?? raw.inserted_datetime ?? null,
    updatedDatetime: raw.updatedDatetime ?? raw.updated_datetime ?? null,
    accessLevel: raw.accessLevel ?? raw.access_level ?? null,
    clientId: raw.clientId ?? raw.client_id,
    noteType:
      tid != null && !Number.isNaN(tid)
        ? {
            id: tid,
            name: raw.notesTypeName || raw.noteTypeName || raw.typeName || raw.noteType?.name || "",
          }
        : raw.noteType ?? null,
  }
}

// GET /api/v1/client-note/notes?clientIds={clientId}
export const fetchClientNotes = createAsyncThunk(
  "clientNotes/fetch",
  async (clientIds, { rejectWithValue }) => {
    try {
      if (!clientIds || (Array.isArray(clientIds) && clientIds.length === 0)) {
        return []
      }
      const ids = Array.isArray(clientIds) ? clientIds : [clientIds]
      const params = ids.map((id) => `clientIds=${id}`).join("&")
      const data = await get(`client-notes?${params}`)
      return extractClientNotesList(data)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch client notes")
    }
  }
)

// POST /api/v1/client-note/clients/{clientId}/note
export const createClientNote = createAsyncThunk(
  "clientNotes/create",
  async ({ clientId, typeId, title, note }, { rejectWithValue }) => {
    try {
      if (!clientId) throw new Error("clientId is required")
      if (!typeId) throw new Error("typeId is required")
      const body = { typeId, note: note || "" }
      if (title) body.title = title
      const data = await post(`client-notes/clients/${clientId}`, body)
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create note")
    }
  }
)

// PATCH /api/v1/client-note/notes/{noteId}
export const updateClientNote = createAsyncThunk(
  "clientNotes/update",
  async ({ noteId, title, note }, { rejectWithValue }) => {
    try {
      if (!noteId) throw new Error("noteId is required")
      const body = {}
      if (title !== undefined) body.title = title
      if (note !== undefined) body.note = note
      const data = await patch(`client-notes/${noteId}`, body)
      return { noteId, ...data }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update note")
    }
  }
)

// DELETE /api/v1/client-note/notes/{noteId}
export const deleteClientNote = createAsyncThunk(
  "clientNotes/delete",
  async (noteId, { rejectWithValue }) => {
    try {
      if (!noteId) throw new Error("noteId is required")
      await del(`client-notes/${noteId}`)
      return noteId
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete note")
    }
  }
)

const initialState = {
  notes: [],
  status: "idle",
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  error: null,
}

const clientNotesSlice = createSlice({
  name: "clientNotes",
  initialState,
  reducers: {
    clearClientNotes: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientNotes.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchClientNotes.fulfilled, (state, action) => {
        state.status = "succeeded"
        const list = Array.isArray(action.payload) ? action.payload : []
        state.notes = list.map(normalizeClientNoteFromApi)
      })
      .addCase(fetchClientNotes.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(createClientNote.pending, (state) => {
        state.createStatus = "loading"
      })
      .addCase(createClientNote.fulfilled, (state, action) => {
        state.createStatus = "succeeded"
        if (action.payload) {
          state.notes.push(normalizeClientNoteFromApi(action.payload))
        }
      })
      .addCase(createClientNote.rejected, (state, action) => {
        state.createStatus = "failed"
        state.error = action.payload
      })
      .addCase(updateClientNote.pending, (state) => {
        state.updateStatus = "loading"
      })
      .addCase(updateClientNote.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        const noteId = action.payload?.noteId ?? action.payload?.id
        const idx = state.notes.findIndex(
          (n) => n.id === noteId || n.noteId === noteId
        )
        if (idx >= 0) {
          state.notes[idx] = normalizeClientNoteFromApi({
            ...state.notes[idx],
            ...action.payload,
          })
        }
      })
      .addCase(updateClientNote.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.error = action.payload
      })
      .addCase(deleteClientNote.pending, (state) => {
        state.deleteStatus = "loading"
      })
      .addCase(deleteClientNote.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded"
        const removed = action.payload
        state.notes = state.notes.filter(
          (n) => n.id !== removed && n.noteId !== removed
        )
      })
      .addCase(deleteClientNote.rejected, (state, action) => {
        state.deleteStatus = "failed"
        state.error = action.payload
      })
  },
})

export const { clearClientNotes } = clientNotesSlice.actions
export default clientNotesSlice.reducer
